/**
 * Typed module-scope runtime-config holder — the SANCTIONED INTERIM for threading
 * bootstrap/session config into `src/common` utils and `src/domains` services, which
 * cannot import `useAppConfig()`/`useSession()` from `src/app` (layer rules).
 *
 * Populated by `AppBootstrapProvider` (formatting/currency/site and static site config values) and
 * `SessionProvider` (session-scoped date/account flags) in `src/app`. React components
 * in features/pages MUST keep using `useAppConfig()`/`useSession()` (TanStack Query) — NOT this
 * registry — so they re-render when config arrives.
 *
 * Follow-up (ADR 0003, Option A): replace this mutable global with formatter factories +
 * `FormattingProvider` + param-threading. Do not grow this surface beyond the interim.
 */

import {
  DEFAULT_FRACTION_DIGITS,
  FALLBACK_DATE_FORMAT,
  FALLBACK_DATETIME_FORMAT,
  FALLBACK_DECIMAL_SEPARATOR,
  FALLBACK_THOUSAND_SEPARATOR,
  FALLBACK_TIME_FORMAT,
} from "~/common/utils/formatting";

export interface RuntimeBatchingPnaData {
  priceEnabled: boolean;
  availabilityEnabled: boolean;
  batches: number[];
}

export interface RuntimeBatchingLineItemUpdateData {
  enabled: boolean;
  batches: number[];
}

export interface RuntimeBatchingData {
  interval: number;
  sizeResetTime: number;
  pna: RuntimeBatchingPnaData;
  lineItemUpdate: RuntimeBatchingLineItemUpdateData;
}

export interface RuntimeConfig {
  punchoutMode: "OCI" | "CXML" | null;
  currencyIso: string;
  currencyDecimalDigits: number;
  decimalSeparator: string;
  thousandSeparator: string;
  dateFormat: string;
  datetimeFormat: string;
  timeFormat: string;
  timezoneOffset: number;
  formattingLocale: string;
  houseNumberFirstInAddress: boolean;
  showSundayAsDeliveryDay: boolean;
  showDateAsCalendarWeek: boolean;
  phaseOutEnabled: boolean;
  lineLevelCustomerOrderNumberEnabled: boolean;
  packageRestrictionEnabled: boolean;
  multipleAccountSearchEnabled: boolean;
  rddMaxYearsAhead: number;
  salesCampaignDaysLimitRDD: number | undefined;
  deliveryDateOffset: number;
  cartPnaThreshold: number;
  siteUid: string;
  isUsSite: boolean;
  removeSalesPartnerFinder: boolean;
  marketingConsentEnabled: boolean;
  salesPartnerStockIntegrationEnabled: boolean;
  batching: RuntimeBatchingData;
}

const defaults: RuntimeConfig = {
  punchoutMode: null,
  currencyIso: "",
  currencyDecimalDigits: DEFAULT_FRACTION_DIGITS,
  decimalSeparator: FALLBACK_DECIMAL_SEPARATOR,
  thousandSeparator: FALLBACK_THOUSAND_SEPARATOR,
  dateFormat: FALLBACK_DATE_FORMAT,
  datetimeFormat: FALLBACK_DATETIME_FORMAT,
  timeFormat: FALLBACK_TIME_FORMAT,
  timezoneOffset: 0,
  formattingLocale: "en",
  houseNumberFirstInAddress: false,
  showSundayAsDeliveryDay: false,
  showDateAsCalendarWeek: false,
  phaseOutEnabled: false,
  lineLevelCustomerOrderNumberEnabled: false,
  packageRestrictionEnabled: false,
  multipleAccountSearchEnabled: false,
  rddMaxYearsAhead: 2,
  salesCampaignDaysLimitRDD: undefined,
  deliveryDateOffset: 0,
  cartPnaThreshold: 0,
  siteUid: "",
  isUsSite: false,
  removeSalesPartnerFinder: false,
  marketingConsentEnabled: false,
  salesPartnerStockIntegrationEnabled: false,
  // Batching disabled by default — degrades to direct (unbatched) calls until the bootstrap
  // fetch resolves. PNA/lineItemUpdate batch-size arrays need at least one entry.
  batching: {
    interval: 0,
    sizeResetTime: 0,
    pna: { priceEnabled: false, availabilityEnabled: false, batches: [1] },
    lineItemUpdate: { enabled: false, batches: [1] },
  },
};

let runtimeConfig: RuntimeConfig = { ...defaults };

/**
 * Merges a partial patch into the runtime config. Called by the bootstrap/session providers
 * (during render, synchronously, so children rendered in the same pass observe fresh values).
 */
export function setRuntimeConfig(patch: Partial<RuntimeConfig>): void {
  runtimeConfig = { ...runtimeConfig, ...patch };
}

/**
 * Returns the current runtime config. Reads before the bootstrap/session fetch resolves
 * observe the sane defaults above so callers do not crash.
 */
export function getRuntimeConfig(): RuntimeConfig {
  return runtimeConfig;
}
